import { ref } from 'vue'
import { usePrint } from '@shared/composables/usePrint.js'
import { apiClient } from '@shared/utils/apiClient.js'
import { useBilling } from './useBilling.js'

const unwrapSingle = (response, key) => {
  if (!response) return null
  if (response[key]) return response[key]
  if (response.data?.[key]) return response.data[key]
  if (response.data) return response.data
  return response
}

const unwrapList = (response) => {
  if (!response) return []
  if (Array.isArray(response)) return response
  if (Array.isArray(response.data)) return response.data
  return []
}

const normalizePaperSize = (size) => (size === 'A5' ? 'A5' : 'A4')

export function useQuotationPrint() {
  const {
    getQuotation,
    getBusiness,
    getCustomer,
    listBankAccounts,
    getBillingSettings,
  } = useBilling()

  const showPrintModal = ref(false)
  const isPrintLoading = ref(false)
  const printError = ref('')
  const printQuotationData = ref(null)
  const printBusinessData = ref(null)
  const printCustomerData = ref(null)
  const printBankAccounts = ref([])
  const printQuotationId = ref(null)

  const { paperSize, savePaperSize } = usePrint({ defaultSize: 'A4' })

  const loadPaperSize = async () => {
    try {
      const settings = await getBillingSettings()
      if (settings?.paper_size) {
        const configuredSize = normalizePaperSize(settings.paper_size)
        paperSize.value = configuredSize
        savePaperSize(configuredSize)
      } else {
        paperSize.value = normalizePaperSize(paperSize.value)
      }
    } catch {
      paperSize.value = normalizePaperSize(paperSize.value)
    }
  }

  const openQuotationPrint = async (quotationOrId) => {
    const quotationId = typeof quotationOrId === 'object' ? quotationOrId?.id : quotationOrId
    if (!quotationId) return

    showPrintModal.value = true
    isPrintLoading.value = true
    printError.value = ''
    printQuotationData.value = null
    printBusinessData.value = null
    printCustomerData.value = null
    printBankAccounts.value = []
    printQuotationId.value = quotationId

    try {
      const [quotationResponse, businessResponse, bankResponse] = await Promise.all([
        getQuotation(quotationId),
        getBusiness().catch(() => null),
        listBankAccounts().catch(() => null),
        loadPaperSize(),
      ])

      const quotation = unwrapSingle(quotationResponse, 'quotation')
      if (!quotation) {
        throw new Error('Quotation not found')
      }

      printQuotationData.value = quotation
      printBusinessData.value = unwrapSingle(businessResponse, 'business')
      printBankAccounts.value = unwrapList(bankResponse)

      if (quotation.customer_id) {
        const customerResponse = await getCustomer(quotation.customer_id).catch(() => null)
        printCustomerData.value = unwrapSingle(customerResponse, 'customer')
      }
    } catch (error) {
      console.error('Failed to prepare quotation print:', error)
      printError.value = error.message || 'Failed to prepare quotation print'
    } finally {
      isPrintLoading.value = false
    }
  }

  const closePrintModal = () => {
    showPrintModal.value = false
  }

  const fetchQuotationPdfBlob = async (quotationId) => {
    const response = await apiClient.client.get(
      `/v1/billing/quotations/${quotationId}/pdf?paper_size=${paperSize.value}`,
      { responseType: 'blob' }
    )
    return response.data
  }

  const printQuotation = async () => {
    if (!printQuotationId.value) return

    try {
      const blob = await fetchQuotationPdfBlob(printQuotationId.value)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (error) {
      console.error('Quotation print failed:', error)
      printError.value = error.message || 'Failed to print quotation'
    }
  }

  return {
    showPrintModal,
    isPrintLoading,
    printError,
    printQuotationData,
    printBusinessData,
    printCustomerData,
    printBankAccounts,
    paperSize,
    openQuotationPrint,
    closePrintModal,
    printQuotation,
  }
}
